import cx from 'classnames'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Styles from './InstagramReelPreview.module.scss'
import { InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { LikeIcon, CommentIcon, ShareIcon, MusicIcon, CheckIcon } from '../InstagramPreview/icons'
import { useTruncateText } from '../hooks/useTruncateText'
import { highlightContent } from '../highlightContent'
import { MediaObject } from 'types'
import { ReactNode } from 'react'
import { MoreDots } from 'blaze-ui/icons/24'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { isVideo } from 'components/ImageMenu/utils'

interface InstagramReelPreviewProps {
  integration?: InstagramIntegration
  mediaObjects?: MediaObject[]
  content?: string
  showVideoControls?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  isGenerating?: boolean
  onClose?: () => void
  alwaysTruncateCaption?: boolean
}

interface MoreProps {
  isTruncated: boolean
  setIsTruncated: React.Dispatch<React.SetStateAction<boolean>>
}

const MoreButton = ({ isTruncated, setIsTruncated }: MoreProps) => {
  return (
    <button className={cx(Styles.moreButton)} onClick={() => setIsTruncated(!isTruncated)}>
      {isTruncated ? 'more' : 'less'}
    </button>
  )
}

export const InstagramReelPreview = ({
  integration,
  mediaObjects,
  content,
  showVideoControls,
  onEdit,
  containerClassName,
  customPreviewContent,
  isActive = true,
  isGenerating = false,
  isTransitioning = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  alwaysTruncateCaption = false,
}: InstagramReelPreviewProps) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: content || '',
    truncatedByDefault: alwaysTruncateCaption,
  })

  const showMoreButton = content && shouldShowMoreButton
  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          almanacFileKey={almanacFileKey as string}
          borderRadius={0}
          position={video ? 'top' : undefined}
          onClose={onClose}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="instagram-video"
              username={username}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>

        <div className={Styles.footer}>
          <div className={Styles.nameAndCaption}>
            <div className={Styles.nameRow}>
              <img src={avatar || Silhouette} alt={username} className={Styles.avatar} />
              <div className={Styles.name}>{name}</div>
              <CheckIcon />
              <div className={cx(Styles.name, Styles.follow)}>Follow</div>
              {onEdit && (
                <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                  Edit
                </Button>
              )}
            </div>
            <div className={Styles.caption}>
              <span
                ref={textRef as React.RefObject<HTMLSpanElement>}
                key={`caption-${content || ''}`}
                className={cx({ [Styles.truncated]: isTruncated })}
                dangerouslySetInnerHTML={{
                  __html: highlightContent(content || '', highlightClassNames),
                }}
              />
              {showMoreButton && !alwaysTruncateCaption && isTruncated && (
                <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
              )}
            </div>
            {showMoreButton && !alwaysTruncateCaption && !isTruncated && (
              <div className={Styles.lessButton}>
                <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
              </div>
            )}
          </div>
        </div>
        <div className={Styles.actions}>
          <div className={Styles.action}>
            <LikeIcon />
            <span>223</span>
          </div>
          <div className={Styles.action}>
            <CommentIcon />
            <span>23</span>
          </div>
          <div className={Styles.action}>
            <ShareIcon />
            <span>3</span>
          </div>
          <div className={Styles.action}>
            <MoreDots />
          </div>
          <div className={Styles.action}>
            <MusicIcon />
          </div>
        </div>
      </div>
    </ConditionalWrapper>
  )
}
